<template>
  <div class="page boss-page project-portfolio-page">
    <van-nav-bar title="战略项目" left-arrow fixed placeholder @click-left="$router.back()"/>

    <section class="guide">
      <b>战略承接项目</b>
      <p>先在「战略落地」维护公司/事业部战略和 OKR，再从这里创建承接项目。</p>
      <button type="button" class="guide-link" @click="$router.push({name:'BossStrategyExecution'})">
        先去维护战略/OKR<van-icon name="arrow"/>
      </button>
    </section>

    <button class="create-entry" @click="openCreate">
      <van-icon name="plus"/>
      <span><b>新建战略项目</b><small>选择事业部、承接 OKR 并指定负责人</small></span>
      <em>新建</em>
    </button>

    <van-loading v-if="loading" class="loading" color="#c62828">加载中</van-loading>
    <template v-else>
      <section class="metrics">
        <div><span>项目数</span><b>{{ activeProjects.length }}</b></div>
        <div><span>当前投入</span><b>{{ money(data.totalInvestment) }}</b></div>
        <div><span>产生价值</span><b>{{ money(data.totalValue) }}</b></div>
        <div><span>风险项目</span><b class="risk">{{ data.riskProjectCount || 0 }}</b></div>
      </section>

      <section v-if="data.resourceConflicts && data.resourceConflicts.length" class="warning">
        <h3><van-icon name="warning-o"/> 资源冲突</h3>
        <p v-for="x in data.resourceConflicts" :key="x.employeeId">
          <b>{{ x.employeeName }}</b> 投入 {{ x.totalAllocationPercent }}%
          <small>{{ x.recommendation }}</small>
        </p>
      </section>

      <section class="projects panel">
        <header>
          <h2>执行中的项目</h2>
          <small>拆任务、分配人员、跟踪投效</small>
        </header>
        <van-empty v-if="!activeProjects.length" description="还没有战略承接项目">
          <van-button plain type="danger" size="small" @click="openCreate">立即创建</van-button>
        </van-empty>
        <button v-for="p in activeProjects" :key="p.id" class="project-row" @click="goDetail(p.id)">
          <van-icon name="orders-o"/>
          <span>
            <b>{{ p.name }}</b>
            <small>{{ p.projectCode }} · {{ priority(p.priority) }}</small>
          </span>
          <em class="stats">
            <b>{{ p.roiPercent == null ? '--' : p.roiPercent + '%' }}</b>
            <small :class="level(p.healthLevel)">{{ health(p.healthLevel) }}</small>
          </em>
          <i>进入</i>
        </button>
      </section>
    </template>

    <van-popup v-model="showCreate" position="bottom" round class="popup" get-container="body">
      <header class="popup-title"><b>根据战略创建项目</b><small>按顺序选择，后续选项会自动联动</small></header>
      <div class="steps">
        <span :class="{done: form.businessUnit}">1 事业部</span>
        <span :class="{done: form.strategyNodeId}">2 战略/OKR</span>
        <span :class="{done: form.ownerEmployeeId}">3 负责人</span>
      </div>
      <van-field readonly clickable required label="所属事业部" :value="form.businessUnit" placeholder="先选择事业部" right-icon="arrow" @click="showBusinessUnit=true"/>
      <van-field readonly clickable required label="承接目标" :disabled="!form.businessUnit" :value="selectedStrategyName" :placeholder="form.businessUnit?'选择该事业部战略或 OKR':'请先选择事业部'" right-icon="arrow" @click="openStrategyPicker"/>
      <van-field readonly clickable required label="项目负责人" :disabled="!form.businessUnit" :value="selectedOwnerName" :placeholder="form.businessUnit?'优先显示本事业部员工':'请先选择事业部'" right-icon="arrow" @click="form.businessUnit&&(showOwner=true)"/>
      <div class="divider">项目信息</div>
      <van-field v-model.trim="form.name" required label="项目名称" placeholder="一句话说明要完成的项目"/>
      <van-field v-model="form.description" type="textarea" rows="3" label="项目描述" placeholder="说明项目要解决的问题、交付结果和边界"/>
      <van-field v-model.trim="form.projectCode" label="项目编号" placeholder="已自动生成，可修改"/>
      <van-field v-model="form.plannedEndDate" required label="完成日期" type="date"/>
      <van-field v-model="form.plannedBudget" type="number" label="计划预算" placeholder="选填"/>
      <van-button block type="danger" class="popup-submit" :loading="saving" @click="create">创建项目并进入任务拆分</van-button>
    </van-popup>
    <van-popup v-model="showBusinessUnit" position="bottom" round get-container="body">
      <van-picker show-toolbar title="选择事业部" :columns="unitOptions.map(x=>x.name)" @cancel="showBusinessUnit=false" @confirm="chooseBusinessUnit"/>
    </van-popup>
    <van-popup v-model="showStrategy" position="bottom" round get-container="body">
      <van-picker show-toolbar title="选择战略或 OKR" :columns="strategyColumns" @cancel="showStrategy=false" @confirm="chooseStrategy"/>
    </van-popup>
    <van-popup v-model="showOwner" position="bottom" round get-container="body">
      <van-picker show-toolbar title="选择项目负责人" :columns="ownerColumns" @cancel="showOwner=false" @confirm="chooseOwner"/>
    </van-popup>
  </div>
</template>
<script>
import coreapi from '@/utils/coreapi';
import {buildStrategyUnitOptions, collectBusinessUnitRows, normalizeOrganizationId, matchesBusinessUnit} from '@/utils/businessUnits';

export default {
  name: 'BossProjectPortfolio',
  data() {
    return {
      loading: false,
      saving: false,
      showCreate: false,
      showBusinessUnit: false,
      showStrategy: false,
      showOwner: false,
      data: {},
      strategies: [],
      employees: [],
      unitOptions: [],
      form: this.initialForm()
    };
  },
  computed: {
    projects() {
      return this.data.projects || [];
    },
    activeProjects() {
      return this.projects.filter(p => {
        const s = String(p.status || '').toUpperCase();
        return !s || s === 'ACTIVE' || s === 'PLANNED';
      });
    },
    filteredStrategies() {
      const unit = this.selectedUnitOption();
      if (!unit) return this.strategies;
      return this.strategies.filter(x => matchesBusinessUnit(x, unit));
    },
    filteredOwners() {
      const exact = this.employees.filter(x => this.unitName(x) === this.form.businessUnit);
      return exact.length ? exact : this.employees;
    },
    strategyColumns() {
      return this.filteredStrategies.map(x => `${this.typeName(x.nodeType || x.node_type)} · ${x.name}`);
    },
    ownerColumns() {
      return this.filteredOwners.map(x => `${x.employeeName} · ${x.positionName || '未配置岗位'}`);
    },
    selectedStrategyName() {
      const x = this.strategies.find(v => Number(v.id) === Number(this.form.strategyNodeId));
      return x ? `${this.typeName(x.nodeType || x.node_type)} · ${x.name}` : '';
    },
    selectedOwnerName() {
      const x = this.employees.find(v => Number(v.employeeId) === Number(this.form.ownerEmployeeId));
      return x ? `${x.employeeName} · ${x.positionName || ''}` : '';
    }
  },
  mounted() {
    this.load().then(() => {
      if (this.$route.query.create === '1') this.openCreate();
    });
  },
  methods: {
    initialForm() {
      const now = new Date();
      const end = new Date(now.getFullYear(), now.getMonth() + 1, now.getDate());
      const fmt = d => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      return {
        businessUnit: '',
        organizationId: null,
        businessUnitSysno: null,
        projectCode: `PJ-${fmt(now).replace(/-/g, '')}-${String(now.getHours()).padStart(2, '0')}${String(now.getMinutes()).padStart(2, '0')}`,
        name: '',
        description: '',
        ownerEmployeeId: null,
        strategyNodeId: null,
        startDate: fmt(now),
        plannedEndDate: fmt(end),
        plannedBudget: ''
      };
    },
    goDetail(id) {
      this.$router.push({name: 'BossProjectDetail', params: {id}});
    },
    async load() {
      this.loading = true;
      try {
        this.data = await coreapi.bossProjectPortfolio();
      } catch (e) {
        this.$toast.fail(e.message || '加载失败');
      } finally {
        this.loading = false;
      }
    },
    async openCreate() {
      this.form = this.initialForm();
      this.showCreate = true;
      try {
        const [map, employees] = await Promise.all([coreapi.strategyActionMap(), coreapi.bossEmployees()]);
        await this.loadUnitOptions();
        this.strategies = ((map && map.nodes) || []).filter(x => {
          const type = String(x.nodeType || x.node_type || '').toUpperCase();
          return type === 'BUSINESS_UNIT_STRATEGY' || type === 'BUSINESS_UNIT_OKR';
        });
        this.employees = employees || [];
        if (this.unitOptions.length === 1) this.chooseBusinessUnit(this.unitOptions[0].name, 0);
      } catch (e) {
        this.$toast.fail(e.message || '创建信息加载失败');
      }
    },
    strategyOrganizationId(node) {
      return normalizeOrganizationId(node && (node.organization_id != null ? node.organization_id : node.organizationId));
    },
    canonicalUnitName(name) {
      const raw = String(name || '').trim();
      if (/安全/.test(raw)) return '安全事业部';
      if (/健康/.test(raw)) return '健康事业部';
      if (/传媒|文化/.test(raw)) return '传媒事业部';
      return raw;
    },
    unitName(x) {
      const name = x.businessUnitName || x.business_unit_name || x.businessUnit || x.business_unit || x.departmentName || '';
      return this.canonicalUnitName(name.indexOf('文化') >= 0 ? '传媒事业部' : name);
    },
    selectedUnitOption() {
      if (!this.form.businessUnit && !this.form.businessUnitSysno) return null;
      return this.unitOptions.find(x => x.businessUnitSysno === this.form.businessUnitSysno)
        || this.unitOptions.find(x => x.name === this.form.businessUnit)
        || {name: this.form.businessUnit, id: this.form.organizationId, businessUnitSysno: this.form.businessUnitSysno};
    },
    async loadUnitOptions() {
      try {
        let rawRows = [];
        try {
          const master = await coreapi.organizationBusinessUnits();
          rawRows = collectBusinessUnitRows(master);
        } catch (e) { /* 回退组织树 */ }
        if (!rawRows.length) {
          const tree = await coreapi.organizationTree();
          rawRows = collectBusinessUnitRows(tree);
        }
        this.unitOptions = buildStrategyUnitOptions(rawRows);
      } catch (e) {
        this.unitOptions = buildStrategyUnitOptions([]);
      }
    },
    openStrategyPicker() {
      if (!this.form.businessUnit) return;
      if (!this.filteredStrategies.length) {
        this.$toast('该事业部暂无已发布的战略或 OKR，请先在「战略落地」发布');
        return;
      }
      this.showStrategy = true;
    },
    chooseBusinessUnit(value, index) {
      const unit = this.unitOptions[index];
      this.form.businessUnit = unit ? unit.name : value;
      this.form.organizationId = unit ? unit.id : null;
      this.form.businessUnitSysno = unit ? unit.businessUnitSysno : null;
      this.form.strategyNodeId = null;
      this.form.ownerEmployeeId = null;
      this.showBusinessUnit = false;
      if (this.filteredStrategies.length === 1) this.form.strategyNodeId = this.filteredStrategies[0].id;
    },
    chooseStrategy(value, index) {
      this.form.strategyNodeId = this.filteredStrategies[index].id;
      this.showStrategy = false;
    },
    chooseOwner(value, index) {
      this.form.ownerEmployeeId = this.filteredOwners[index].employeeId;
      this.showOwner = false;
    },
    async create() {
      if (!this.form.businessUnit) return this.$toast('请选择所属事业部');
      if (!this.form.strategyNodeId) return this.$toast('请选择承接战略或 OKR');
      if (!this.form.ownerEmployeeId) return this.$toast('请选择项目负责人');
      if (!this.form.name) return this.$toast('请填写项目名称');
      if (!this.form.plannedEndDate) return this.$toast('请选择完成日期');
      this.saving = true;
      try {
        const f = this.form;
        const p = await coreapi.createBossProject({
          ...f,
          ownerEmployeeId: Number(f.ownerEmployeeId),
          strategyNodeId: Number(f.strategyNodeId),
          plannedBudget: Number(f.plannedBudget || 0),
          status: 'ACTIVE',
          priority: 'MEDIUM',
          revenueTarget: 0,
          leadTarget: 0,
          dealTarget: 0
        });
        this.showCreate = false;
        this.$router.push({name: 'BossProjectTaskPlanning', params: {id: p.id}, query: {newProject: '1'}});
      } catch (e) {
        this.$toast.fail(e.message || '创建失败');
      } finally {
        this.saving = false;
      }
    },
    typeName(v) {
      return v === 'BUSINESS_UNIT_OKR' ? '事业部 OKR' : '事业部战略';
    },
    money(v) {
      return '¥' + Number(v || 0).toLocaleString();
    },
    level(v) {
      return String(v || '').toLowerCase();
    },
    health(v) {
      return {HEALTHY: '健康', WATCH: '关注', RISK: '风险'}[v] || '待计算';
    },
    priority(v) {
      return {HIGH: '高优先级', MEDIUM: '中优先级', LOW: '低优先级'}[v] || v;
    }
  }
};
</script>
<style lang="less" scoped>
@import './boss.less';

.project-portfolio-page {
  min-height: 100vh;
  margin: 0;
  padding-bottom: 1.2rem;
  background: #f6f7fb;
  color: #292d3d;
}

.project-portfolio-page > .van-nav-bar.van-nav-bar--fixed,
.project-portfolio-page > .van-nav-bar,
.project-portfolio-page /deep/ .van-nav-bar {
  top: 0 !important;
  left: 0 !important;
  margin: 0 !important;
  width: 100vw !important;
  max-width: 100% !important;
  border-radius: 0 !important;
}

.guide {
  margin: .2rem .28rem 0;
  padding: .24rem .26rem;
  background: #fff;
  border-radius: .16rem;
  box-shadow: 0 .05rem .2rem rgba(30, 35, 60, .06);
}

.guide b {
  font-size: .3rem;
  color: #303344;
}

.guide p {
  margin: .08rem 0 0;
  color: #73778a;
  font-size: .22rem;
  line-height: 1.5;
}

.guide-link {
  margin-top: .12rem;
  padding: 0;
  display: inline-flex;
  align-items: center;
  gap: .04rem;
  color: #c6282d;
  font-size: .23rem;
  border: 0;
  background: transparent;
  cursor: pointer;
}

.create-entry {
  width: calc(100% - .56rem);
  margin: .2rem .28rem 0;
  min-height: 1.28rem;
  padding: .24rem .22rem;
  display: flex;
  align-items: center;
  gap: .16rem;
  text-align: left;
  border: 0;
  border-radius: .16rem;
  background: #fff;
  box-shadow: 0 .05rem .2rem rgba(30, 35, 60, .06);
  cursor: pointer;
}

.create-entry:active {
  background: #fafafa;
}

.create-entry > .van-icon:first-child {
  width: .72rem;
  height: .72rem;
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 .72rem;
  color: #c62828;
  font-size: .38rem;
  border-radius: .14rem;
  background: #fff5f5;
}

.create-entry span {
  min-width: 0;
  flex: 1;
}

.create-entry b,
.create-entry small {
  display: block;
}

.create-entry b {
  font-size: .3rem;
  color: #34364a;
  font-weight: 600;
}

.create-entry small {
  margin-top: .06rem;
  color: #8b8e9d;
  font-size: .22rem;
}

.create-entry em {
  flex: 0 0 auto;
  min-width: .88rem;
  padding: .1rem .24rem;
  color: #fff;
  text-align: center;
  border-radius: .28rem;
  background: linear-gradient(135deg, #d32f2f, #b71c1c);
  font-size: .24rem;
  font-style: normal;
  font-weight: 500;
  box-shadow: 0 .04rem .12rem rgba(198, 40, 40, .25);
}

.loading {
  padding: 2rem;
  text-align: center;
}

.metrics {
  margin: .2rem .28rem 0;
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  background: #fff;
  border-radius: .16rem;
  box-shadow: 0 .05rem .2rem rgba(30, 35, 60, .06);
}

.metrics div {
  padding: .24rem .08rem;
  text-align: center;
  border-right: 1PX solid #f0f0f0;
}

.metrics div:last-child {
  border-right: 0;
}

.metrics span,
.metrics b {
  display: block;
}

.metrics span {
  color: #8b8e9e;
  font-size: .22rem;
}

.metrics b {
  margin-top: .08rem;
  font-size: .3rem;
  color: #34364a;
}

.risk {
  color: #c62828 !important;
}

.warning {
  margin: .2rem .28rem 0;
  padding: .24rem .26rem;
  background: #fff;
  border-radius: .16rem;
  border-left: .06rem solid #e59b23;
  box-shadow: 0 .05rem .2rem rgba(30, 35, 60, .06);
}

.warning h3 {
  margin: 0;
  display: flex;
  align-items: center;
  gap: .08rem;
  font-size: .28rem;
  color: #8b642b;
}

.warning p {
  margin: .1rem 0 0;
  font-size: .24rem;
  color: #666;
}

.warning small {
  display: block;
  margin-top: .04rem;
  color: #8b6a25;
  font-size: .22rem;
}

.panel {
  margin: .2rem .28rem 0;
  padding: .24rem .26rem .08rem;
  background: #fff;
  border-radius: .16rem;
  box-shadow: 0 .05rem .2rem rgba(30, 35, 60, .06);
}

.panel header {
  margin-bottom: .08rem;
}

.panel h2 {
  margin: 0;
  font-size: .32rem;
  color: #30313a;
}

.panel header small {
  display: block;
  margin-top: .04rem;
  color: #8b8e9d;
  font-size: .22rem;
}

.project-row {
  width: 100%;
  min-height: 1.2rem;
  padding: .2rem 0;
  display: flex;
  align-items: center;
  gap: .14rem;
  text-align: left;
  border: 0;
  border-top: 1PX solid #f0f0f0;
  background: #fff;
  cursor: pointer;
}

.project-row:first-of-type {
  border-top: 0;
}

.project-row > .van-icon:first-child {
  width: .64rem;
  height: .64rem;
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 .64rem;
  color: #c62828;
  font-size: .34rem;
  border-radius: .12rem;
  background: #fff5f5;
}

.project-row > span {
  min-width: 0;
  flex: 1;
}

.project-row b,
.project-row small {
  display: block;
}

.project-row > span b {
  font-size: .29rem;
  color: #34364a;
}

.project-row > span small {
  margin-top: .05rem;
  color: #8b8e9d;
  font-size: .22rem;
}

.project-row .stats {
  flex: 0 0 auto;
  margin-right: .06rem;
  text-align: right;
  font-style: normal;
}

.project-row .stats b {
  font-size: .28rem;
  color: #c62828;
}

.project-row .stats small {
  margin-top: .04rem;
  font-size: .2rem;
  color: #8b8e9d;
}

.project-row .stats small.healthy {
  color: #278e56;
}

.project-row .stats small.watch {
  color: #d9891b;
}

.project-row .stats small.risk {
  color: #c62828;
}

.project-row > i {
  flex: 0 0 auto;
  min-width: .8rem;
  padding: .08rem .2rem;
  color: #fff;
  font-style: normal;
  font-size: .22rem;
  font-weight: 500;
  text-align: center;
  border-radius: .24rem;
  background: linear-gradient(135deg, #d32f2f, #b71c1c);
}

.popup {
  padding: .32rem .28rem .5rem;
  max-height: 88vh;
  overflow: auto;
}

.popup-title b,
.popup-title small {
  display: block;
}

.popup-title b {
  font-size: .36rem;
  color: #30313a;
}

.popup-title small {
  margin-top: .08rem;
  color: #8b8e9e;
  font-size: .24rem;
}

.steps {
  margin: .25rem 0;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: .08rem;
}

.steps span {
  padding: .12rem .05rem;
  text-align: center;
  color: #9497a5;
  font-size: .22rem;
  border-radius: .08rem;
  background: #f1f2f5;
}

.steps .done {
  color: #fff;
  background: #c6282d;
}

.divider {
  margin: .25rem 0 .08rem;
  color: #8b8e9e;
  font-size: .23rem;
}

.popup-submit {
  margin-top: .3rem;
  height: .88rem;
  border-radius: .12rem;
  font-size: .28rem;
  font-weight: 500;
}
</style>



// WEBPACK FOOTER //
// src/views/boss/ProjectPortfolio.vue